import {
  Component,
  HostListener,
  Input,
  SimpleChanges,
  ViewChild,
  inject,
  NgModule,
  computed,
} from "@angular/core";
import { UploadComponent } from "@shared-features/upload/upload.component";
import { UploadProcessResponse } from "@shared-interfaces/shared-upload";
import { FileData } from "@shared-types/shared-file";
import { LoaderService } from "@shared-utils/loader/loader.service";
import { ProcessService } from "@shared-api/process/process.service";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { filter } from "rxjs";
import { ProcessResponse } from "@shared-interfaces/shared-process";
import { MatSnackBar } from "@angular/material/snack-bar";
import { MatButtonModule } from "@angular/material/button";
import { MatExpansionModule } from "@angular/material/expansion";
import { WorkflowOverviewService } from "../../../home/data-access/workflow-overview.service";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { KeyValuePipe } from "@angular/common";
import { WorkflowType } from "@shared-types/shared-file";
import { UploadedFileInformation } from "@shared-types/shared-upload";
import { MatFormFieldModule } from "@angular/material/form-field";

@Component({
  selector: "prompting-view",
  standalone: true,
  imports: [UploadComponent, MatButtonModule, MatExpansionModule, FormsModule, KeyValuePipe, ReactiveFormsModule, MatFormFieldModule],
  templateUrl: "./prompting-view.component.html",
  styleUrl: "./prompting-view.component.scss",
})
export class PromptingViewComponent {
  workflowService = inject(WorkflowOverviewService);
  private loaderService = inject(LoaderService);
  private processService = inject(ProcessService);
  @Input() workflowId: string | undefined = undefined;

  public data = {
    questionConfig: {},
    workflowId: '',
    workflowType: 'documents' as WorkflowType,
    worfkflowInfo: undefined,
    allowCloseWhileLoading: true,
    fileFormats: [
      'application/pdf',
      '.docx',
      'text/plain',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'application/msword'
    ]
  };

  public hasProcessedFiles = computed(() => {
    const fileList = this.workflowService.fileList()
    return fileList && fileList.length && fileList.reduce((acc, file) => file.status === 'processed' && acc, true)
  })

  ngOnChanges(changes: SimpleChanges): void {
    if (this.workflowService.workflowId()) {
      this.workflowService.updateWorkflowData$.next();
    }
    this.workflowService.getWorkflowData$.next(this.workflowId);
  }

  constructor(private snackBar: MatSnackBar) {
    this.processService.processResults$
      .pipe(
        takeUntilDestroyed(),
        filter((process) => {
          return (['completed', 'failed'].includes(process.status));
        })
      )
      .subscribe((processResults: ProcessResponse) => {
        this.processResultActions(processResults);
      });

  }

  savePrompt() {
    this.workflowService.updateWorkflowData$.next()
  }

  updatePromptContext(newContext: string, type: string): void {
    if (type == "prompt") {
      this.workflowService.updatePromptInformation$.next([
        newContext,
        this.workflowService.promptContext() ?? "",
      ]);
    } else {
      this.workflowService.updatePromptInformation$.next([
        this.workflowService.prompt() ?? "",
        newContext,
      ]);
    }
  }

  testPrompt() {
    this.workflowService.testPrompt$.next([
      this.workflowService.prompt() ?? "",
      this.workflowService.promptContext() ?? ""

    ]);
  }

  processResultActions(process: ProcessResponse): void {
    switch (process.name) {
      case '/file/upload':
        if (this.workflowService.workflowId() && process.status !== "failed") {
          process.status = 'processed';
          this.workflowService.updateFile$.next(process);
        } else {
          process.status = 'failed';
          this.workflowService.updateFile$.next(process);
        }
        break;
      case '/genai':
        this.workflowService.updateContractAnalysis$.next(process);
        break;
      default:
        break;
    }
  }

  @HostListener("window:beforeunload", ["$event"])
  unloadHandler(event: Event) {
    this.workflowService.updateWorkflowData$.next();
  }
  ngOnDestroy(): void {
    this.workflowService.updateWorkflowData$.next();
  }

  updateFileData(event: UploadedFileInformation) {
    this.workflowService.addFileData$.next({
      displayName: event.displayName,
      storedName: event.storedFilename,
      status: 'queued',
      fileExtension: event.process.name.split('.').pop()!,
      uploadedAt: new Date().toISOString(),
    });
    this.loaderService.show();
    this.processService.startPolling$.next(event.process);
    this.cleanUpAnalysisResult();
  }

  public deleteFileData(event: FileData): void {
    this.workflowService.deleteFileData$.next(event);
    this.cleanUpAnalysisResult();
  }
  private cleanUpAnalysisResult(): void {
    this.workflowService.filters.set({ yes: false, no: false, check: false });
    this.workflowService.componentState.update((state => ({
      ...state,
      // @ts-ignore
      workflowData: {
        ...state.workflowData,
        contractResults: undefined,
        activeStep: 1
      }
    })))
  }
}
